import { useState } from "react";
import { BarChart3, ShieldCheck, Sparkles, TrendingUp, CheckSquare } from "lucide-react";
import { FiltersBar, type FiltersBarConfig } from "../components/dashboard/FiltersBar";
import { KPIGrid } from "../components/dashboard/KPIGrid";
import { AIInsightsPanel } from "../components/dashboard/AIInsightsPanel";
import { ProcurementHealth } from "../components/charts/ProcurementHealth";
import { SpendTrendChart } from "../components/charts/SpendTrendChart";
import { CategoryTreemap } from "../components/charts/CategoryTreemap";
import { SpendTrendAnalysis } from "../components/dashboard/SpendTrendAnalysis";
import { PriorityActions } from "../components/dashboard/PriorityActions";
import { SpendCubeCategoryChart } from "../components/dashboard/SpendCubeCategoryChart";
import { SpendEnrichmentPanels } from "../components/dashboard/SpendEnrichmentPanels";
import { PaymentTermsImagePanel } from "../components/dashboard/PaymentTermsImagePanel";
import { filterOverview, overviewTransactions } from "../data/executiveOverview";
import { EXECUTIVE_FILTER_FIELDS } from "../data/executiveFilters";

const executiveFiltersConfig: FiltersBarConfig = {
  title: "Filters",
  fields: EXECUTIVE_FILTER_FIELDS,
  savedViews: ["2025 Overview", "Latin America Risk", "IT Spend Breakdown"],
  savedViewFilters: {
    "2025 Overview": { timeframe: "2025" },
    "Latin America Risk": { geography: "Latin America", contractStatus: "Expired" },
    "IT Spend Breakdown": { category: "Information Technology" },
  },
};

type DashTab = "overview" | "spendCube" | "spendEnrichment" | "paymentTerms" | "spendAiRisk" | "consolidationStrategy";

const DASH_TABS: { id: DashTab; label: string; icon: React.ElementType }[] = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "spendCube", label: "Spend Cube Analysis", icon: TrendingUp },
  { id: "spendEnrichment", label: "Spend Enrichment", icon: Sparkles },
  { id: "paymentTerms", label: "Payment Terms", icon: CheckSquare },
  { id: "spendAiRisk", label: "Spend at Risk", icon: ShieldCheck },
  { id: "consolidationStrategy", label: "Consolidation Strategy", icon: BarChart3 },
];
// Blank tab components for new tabs
function SpendCubeAnalysisPanel({ rows }: { rows: typeof overviewTransactions }) {
  return (
    <div className="dash-panel">
      <SpendCubeCategoryChart rows={rows} />
    </div>
  );
}
function SpendEnrichmentPanel({ rows }: { rows: typeof overviewTransactions }) {
  return (
    <div className="dash-panel">
      <SpendEnrichmentPanels rows={rows} />
    </div>
  );
}
function PaymentTermsPanel({ rows }: { rows: typeof overviewTransactions }) {
  return (
    <div className="dash-panel">
      <PaymentTermsImagePanel rows={rows} />
    </div>
  );
}
import { SpendAiRiskImagePanel } from "../components/dashboard/SpendAiRiskImagePanel";

function SpendAiRiskPanel({ rows }: { rows: typeof overviewTransactions }) {
  return (
    <div className="dash-panel">
      <SpendAiRiskImagePanel rows={rows} />
    </div>
  );
}
import { ConsolidationStrategyImagePanel } from "../components/dashboard/ConsolidationStrategyImagePanel";

function ConsolidationStrategyPanel({ rows }: { rows: typeof overviewTransactions }) {
  return (
    <div className="dash-panel">
      <ConsolidationStrategyImagePanel rows={rows} />
    </div>
  );
}

interface Props {
  onInsights: () => void;
}

/* ── Compliance tab content ─────────────────────────────────── */
function CompliancePanel() {
  const items = [
    { label: "ESG Compliance Score",          value: "82%",  delta: "+4%",  ok: true },
    { label: "Contract Adherence",            value: "91%",  delta: "+1%",  ok: true },
    { label: "Supplier Code of Conduct",      value: "74%",  delta: "-2%",  ok: false },
    { label: "Regulatory Filings On-Time",    value: "98%",  delta: "0%",   ok: true },
    { label: "Diversity Spend Target",        value: "61%",  delta: "+6%",  ok: true },
    { label: "Anti-Bribery Certifications",   value: "88%",  delta: "+3%",  ok: true },
  ];
  return (
    <div className="dash-panel">
      <div className="dashboard-grid" style={{ marginBottom: 16 }}>
        <div className="chart-panel" style={{ flex: 2 }}>
          <div style={{ padding: "16px 20px 8px", borderBottom: "1px solid var(--border)" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>Compliance Overview</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Key compliance KPIs across procurement categories</div>
          </div>
          <div style={{ padding: 16 }}>
            {items.map((item) => (
              <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>{item.label}</div>
                  <div style={{ height: 6, background: "var(--border)", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: item.value, background: item.ok ? "var(--status-success)" : "var(--status-warning)", borderRadius: 3 }} />
                  </div>
                </div>
                <div style={{ textAlign: "right", minWidth: 60 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>{item.value}</div>
                  <div style={{ fontSize: 11, color: item.ok ? "var(--status-success)" : "var(--status-error)" }}>{item.delta}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="health-panel" style={{ flex: 1 }}>
          <div style={{ padding: "16px 20px 8px", borderBottom: "1px solid var(--border)" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>Risk Alerts</div>
          </div>
          <div style={{ padding: 12 }}>
            {[
              { msg: "3 suppliers missing ESG certificates", sev: "#f59e0b" },
              { msg: "2 contracts expiring within 30 days", sev: "#ef4444" },
              { msg: "Diversity spend below Q4 target", sev: "#f59e0b" },
              { msg: "Anti-bribery training overdue (5 suppliers)", sev: "#3265aa" },
            ].map((a) => (
              <div key={a.msg} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid var(--border-light)" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: a.sev, marginTop: 4, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: "var(--text-primary)" }}>{a.msg}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Risk tab ────────────────────────────────────────────────── */
function RiskPanel() {
  return (
    <div className="dash-panel">
      <div className="dashboard-grid">
        <SpendTrendAnalysis />
        <ProcurementHealth />
      </div>
      <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", marginTop: 0 }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>High-Risk Suppliers</div>
          <span style={{ fontSize: 11, color: "var(--status-error)", background: "rgba(239,68,68,0.1)", padding: "2px 8px", borderRadius: 9999, fontWeight: 700 }}>12 flagged</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "var(--surface-page)" }}>
                {["Supplier", "Category", "Risk Score", "Issues", "Status"].map((h) => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { name: "Acme Logistics", cat: "Transportation", score: 87, issues: "Late deliveries, sanctions", status: "Critical" },
                { name: "Global Tech Supplies", cat: "IT Hardware", score: 72, issues: "ESG non-compliance", status: "High" },
                { name: "ProServ Consulting", cat: "Professional Services", score: 65, issues: "Contract dispute", status: "High" },
                { name: "EuroChemicals Ltd", cat: "Raw Materials", score: 61, issues: "Regulatory filing", status: "Medium" },
              ].map((r) => (
                <tr key={r.name} style={{ borderBottom: "1px solid var(--border-light)" }}>
                  <td style={{ padding: "8px 12px", fontWeight: 600 }}>{r.name}</td>
                  <td style={{ padding: "8px 12px", color: "var(--text-secondary)" }}>{r.cat}</td>
                  <td style={{ padding: "8px 12px" }}>
                    <span style={{ fontWeight: 700, color: r.score > 80 ? "#ef4444" : r.score > 60 ? "#f59e0b" : "#10b981" }}>{r.score}</span>
                  </td>
                  <td style={{ padding: "8px 12px", color: "var(--text-secondary)" }}>{r.issues}</td>
                  <td style={{ padding: "8px 12px" }}>
                    <span style={{
                      padding: "2px 8px", borderRadius: 9999, fontSize: 10, fontWeight: 700,
                      background: r.status === "Critical" ? "rgba(239,68,68,0.12)" : r.status === "High" ? "rgba(245,158,11,0.12)" : "rgba(59,130,246,0.12)",
                      color: r.status === "Critical" ? "#ef4444" : r.status === "High" ? "#f59e0b" : "#3b82f6",
                    }}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ── Savings tab ────────────────────────────────────────────── */
function SavingsPanel({ onInsights }: { onInsights: () => void }) {
  return (
    <div className="dash-panel">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12, marginBottom: 16 }}>
        {[
          { label: "Total Opportunity Value", value: "$4.2M",  color: "#6b3fa0" },
          { label: "Quick Wins (<30 days)",   value: "$820K",  color: "#10b981" },
          { label: "High Confidence (>90%)",  value: "8",      color: "#3265aa" },
          { label: "Avg. Confidence Score",   value: "87%",    color: "#f59e0b" },
        ].map((m) => (
          <div key={m.label} className="metric-card">
            <div className="metric-card__label">{m.label}</div>
            <div className="metric-card__value" style={{ color: m.color }}>{m.value}</div>
          </div>
        ))}
      </div>
      <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Top Opportunities</div>
          <button
            type="button"
            onClick={onInsights}
            style={{ fontSize: 12, color: "var(--brand-blue)", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
          >
            View All →
          </button>
        </div>
        <div style={{ padding: 16 }}>
          {[
            { title: "Supplier Consolidation — IT Hardware", value: "$1.2M", conf: 94, type: "Consolidation" },
            { title: "Contract Renegotiation — Logistics", value: "$680K", conf: 88, type: "Contract" },
            { title: "Price Variance — Raw Materials", value: "$540K", conf: 91, type: "Price" },
            { title: "Catalog Standardization", value: "$420K", conf: 85, type: "Catalog" },
          ].map((o) => (
            <div key={o.title} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid var(--border-light)" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", marginBottom: 2 }}>{o.title}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{o.type} · {o.conf}% confidence</div>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--status-success)", minWidth: 60, textAlign: "right" }}>{o.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ExecutiveDashboardView({ onInsights }: Props) {
  const [activeTab, setActiveTab] = useState<DashTab>("overview");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const rows = filterOverview(overviewTransactions, filters);
  const now = new Date(overviewTransactions[0].Last_Updated).toLocaleDateString("en-US", { dateStyle: "medium" });

  return (
    <>
      {/* Breadcrumb */}
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <span>Spend</span>
        <span className="breadcrumb__sep" aria-hidden="true">/</span>
        <span className="breadcrumb__current">Executive Dashboard</span>
      </nav>

      {/* Page header */}
      <div className="page-header">
        <h2 className="page-header__title">Executive Dashboard</h2>
        <p className="page-header__description">
          Real-time AI-powered spend intelligence across all active procurement projects.
        </p>
      </div>

      <div className="dashboard-sync" aria-label={`Last synced: ${now}`}>
        Last synced: {now}
      </div>

      {/* Filters */}
      <FiltersBar config={executiveFiltersConfig} onFiltersChange={setFilters} />

      {/* Dashboard Tabs */}
      <div className="dash-tabs" role="tablist">
        {DASH_TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activeTab === id}
            className={`dash-tab${activeTab === id ? " is-active" : ""}`}
            onClick={() => setActiveTab(id)}
          >
            <Icon size={17} aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Panels */}
      {activeTab === "overview" && (
        <div className="dash-panel">
          {/* KPI Grid */}
          <KPIGrid rows={rows} />

          {/* AI Insights + Procurement Health */}
          <div className="dashboard-grid">
            <AIInsightsPanel onViewAll={onInsights} rows={rows} />
            <ProcurementHealth rows={rows} />
          </div>

          {/* Spend Trend + Category Treemap */}
          <div className="dashboard-grid">
            <div className="chart-panel" style={{ width: '100%' }}>
              {/* Title and Legend Row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div className="chart-panel__title" style={{ marginBottom: 0 }}>Spend Trend</div>
                <div style={{
                  display: 'flex', gap: 18, alignItems: 'center', padding: '6px 16px', fontFamily: 'FSAlbert-Bold'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#444', display: 'inline-block' }} />
                    <span style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>Spend</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#3265aa', display: 'inline-block' }} />
                    <span style={{ fontSize: 12, color: '#3265aa', fontWeight: 600 }}>Budget</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#e0044e', display: 'inline-block' }} />
                    <span style={{ fontSize: 12, color: '#e0044e', fontWeight: 600 }}>Savings</span>
                  </span>
                </div>
              </div>
              <div className="chart-panel__sub">Monthly spend across the selected years (USD millions)</div>
              <SpendTrendChart rows={rows} />
            </div>
            <CategoryTreemap rows={rows} />
          </div>

          {/* Spend Trend Analysis + Priority Actions */}
          <div className="dashboard-grid">
            <div style={{ flex: 1, minWidth: 0 }}>
              <SpendTrendAnalysis rows={rows} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <PriorityActions rows={rows} />
            </div>
          </div>
        </div>
      )}

      {activeTab === "spendCube" && <SpendCubeAnalysisPanel rows={rows} />}
      {activeTab === "spendEnrichment" && <SpendEnrichmentPanel rows={rows} />}
      {activeTab === "paymentTerms" && <PaymentTermsPanel rows={rows} />}
      {activeTab === "spendAiRisk" && <SpendAiRiskPanel rows={rows} />}
      {activeTab === "consolidationStrategy" && <ConsolidationStrategyPanel rows={rows} />}
    </>
  );
}
